import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Heart, MessageCircle, Share2, X, Bell, MessageSquare, Search, UserPlus, UserCheck } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';
import PostCard from '@/components/PostCard';
import DailyStatBanner from '@/components/DailyStatBanner';
import RoutineReminderBanner from '@/components/RoutineReminderBanner';
import GlowBadge from '@/components/GlowBadge';

const PAGE_SIZE = 10;

export default function FeedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showStat, setShowStat] = useState(true);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [suggestedProfiles, setSuggestedProfiles] = useState([]);
  const loaderRef = useRef(null);

  useEffect(() => {
    fetchUser();
    fetchPosts(0);
    fetchNotifications();
    fetchUnreadMessages();
    fetchSuggestedProfiles();
  }, []);

  // Re-fetch suggested profiles every time the user navigates back to this page
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchSuggestedProfiles();
  }, [location.key]);

  // Infinite scroll observer
  useEffect(() => {
    if (!loaderRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && hasMore) {
          fetchPosts(posts.length);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [posts.length, loadingMore, hasMore]);

  const fetchUser = async () => {
    try {
      const response = await authFetch(`${API}/auth/me`);
      if (response.ok) setUser(await response.json());
    } catch (error) { console.error('Error fetching user:', error); }
  };

  const fetchNotifications = async () => {
    try {
      const response = await authFetch(`${API}/notifications/unread-count`);
      if (response.ok) {
        const data = await response.json();
        setUnreadNotifications(data.count || 0);
      }
    } catch (error) { console.error('Error:', error); }
  };

  const fetchUnreadMessages = async () => {
    try {
      const response = await authFetch(`${API}/messages/unread-count`);
      if (response.ok) {
        const data = await response.json();
        setUnreadMessages(data.count || 0);
      }
    } catch (error) { console.error('Error:', error); }
  };

  const fetchPosts = async (skip) => {
    const isInitial = skip === 0;
    if (isInitial) setLoading(true);
    else setLoadingMore(true);

    try {
      const response = await authFetch(`${API}/posts?skip=${skip}&limit=${PAGE_SIZE}`);
      if (response.ok) {
        const newPosts = await response.json();
        if (isInitial) {
          setPosts(newPosts);
        } else {
          setPosts(prev => [...prev, ...newPosts]);
        }
        setHasMore(newPosts.length === PAGE_SIZE);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load posts');
    } finally {
      if (isInitial) setLoading(false);
      else setLoadingMore(false);
    }
  };

  const fetchSuggestedProfiles = async () => {
    try {
      const response = await authFetch(`${API}/suggested-profiles`);
      if (response.ok) setSuggestedProfiles(await response.json());
    } catch (error) { console.error('Error:', error); }
  };

  const handleFollowSuggested = async (userId) => {
    try {
      const response = await authFetch(`${API}/users/${userId}/follow`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        const isRequested = data.status === 'requested';
        setSuggestedProfiles(prev => prev.map(p =>
          p.user_id === userId ? { ...p, is_following: !isRequested, is_requested: isRequested } : p
        ));
        toast.success(isRequested ? 'Follow request sent!' : 'Following!');
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleLike = async (postId) => {
    try {
      const response = await authFetch(`${API}/posts/${postId}/like`, { method: 'POST' });
      if (response.ok) {
        const result = await response.json();
        setPosts(posts.map(post => {
          if (post.post_id === postId) {
            const likes = post.likes || [];
            return {
              ...post,
              likes: result.liked 
                ? [...likes, user.user_id]
                : likes.filter(id => id !== user.user_id)
            };
          }
          return post;
        }));
      }
    } catch (error) { console.error('Error liking post:', error); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-3 py-1.5 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <h1 className="text-2xl font-black gradient-text" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Gloverse
            </h1>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="feed-shop-btn"
              onClick={() => navigate('/shop')}
              className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors"
            >
              Shop
            </button>
            <button
              data-testid="feed-search-btn"
              onClick={() => navigate('/search')}
              className="relative p-2 hover:bg-primary/10 rounded-full transition-colors"
              title="Search"
            >
              <Search className="w-5 h-5" />
            </button>
            <button
              data-testid="feed-messages-btn"
              onClick={() => navigate('/messages')}
              className="relative p-2 hover:bg-primary/10 rounded-full transition-colors"
              title="Messages"
            >
              <MessageSquare className="w-5 h-5" />
              {unreadMessages > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
              )}
            </button>
            <button
              data-testid="feed-notifications-btn"
              onClick={() => navigate('/notifications')}
              className="relative p-2 hover:bg-primary/10 rounded-full transition-colors"
              title="Notifications"
            >
              <Bell className="w-5 h-5" />
              {unreadNotifications > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Urgency Banner */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-center py-1.5 px-4" data-testid="earnings-banner">
        <p className="text-white text-xs font-medium tracking-wide" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Creator earnings beta rolling out soon
        </p>
      </div>

      {/* Daily Stat Banner */}
      {user?.daily_stat_enabled && showStat && (
        <DailyStatBanner onClose={() => setShowStat(false)} />
      )}

      {/* Routine Reminder */}
      <RoutineReminderBanner />

      {/* Feed */}
      <div className="max-w-2xl mx-auto px-4 py-4 space-y-6">
        {posts.length === 0 ? (
          <div className="space-y-6">
            <div className="text-center py-6">
              <p className="text-muted-foreground text-lg mb-2">Your feed is empty</p>
              <p className="text-sm text-muted-foreground">Follow creators to see their posts!</p>
            </div>

            {/* Suggested Profiles */}
            {suggestedProfiles.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>People to Follow</h3>
                  <button
                    data-testid="see-all-creators"
                    onClick={() => navigate('/glow-creators')}
                    className="text-sm text-primary font-medium"
                  >
                    See All
                  </button>
                </div>
                <div className="space-y-3">
                  {suggestedProfiles.slice(0, 8).map(profile => (
                    <div
                      key={profile.user_id}
                      data-testid={`suggested-${profile.user_id}`}
                      className="flex items-center gap-2.5 p-3 rounded-2xl bg-card border border-border"
                    >
                      <button onClick={() => navigate(`/profile/${profile.user_id}`)} className="shrink-0">
                        {profile.picture ? (
                          <img src={profile.picture} alt={profile.name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-sm">
                            {profile.name?.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </button>
                      <button onClick={() => navigate(`/profile/${profile.user_id}`)} className="flex-1 min-w-0 text-left">
                        <p className="font-semibold text-sm truncate">{profile.name}</p>
                        <p className="text-[11px] text-muted-foreground truncate">@{profile.username} · {profile.followers_count} followers</p>
                      </button>
                      {!profile.is_following && !profile.is_requested ? (
                        <button
                          data-testid={`follow-suggested-${profile.user_id}`}
                          onClick={() => handleFollowSuggested(profile.user_id)}
                          className="shrink-0 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold flex items-center gap-1"
                        >
                          <UserPlus className="w-3 h-3" />
                          Follow
                        </button>
                      ) : profile.is_requested ? (
                        <span className="shrink-0 text-[11px] text-muted-foreground px-2.5 py-1.5 rounded-lg bg-muted">
                          Requested
                        </span>
                      ) : (
                        <span className="shrink-0 text-[11px] text-muted-foreground flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-muted">
                          <UserCheck className="w-3 h-3" />
                          Following
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {posts.map(post => (
              <PostCard 
                key={post.post_id} 
                post={post} 
                currentUser={user}
                onLike={handleLike}
              />
            ))}
            {/* Infinite scroll trigger */}
            <div ref={loaderRef} className="py-4 text-center">
              {loadingMore && (
                <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
              )}
              {!hasMore && posts.length > 0 && (
                <p className="text-sm text-muted-foreground">You're all caught up!</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
